import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'de',
  path: '/ausgaben-teilen-paare',
  alternatePath: '/expense-sharing-couples',
  seo: {
    title: 'Ausgaben teilen für Paare — gemeinsame Kosten fair aufteilen | Equna',
    description:
      'Equna ist die App für Paare, die laufende gemeinsame Ausgaben fair aufteilen wollen — mit Verteilungsschlüssel, Belegen und Monatsabschluss. Made in Germany, DSGVO-konform.',
  },
  hero: {
    eyebrow: 'Für Paare',
    h1: 'Ausgaben teilen für Paare — endlich Ruhe beim Thema Geld',
    sub: 'Miete, Einkäufe, Strom, Streamingdienste: Equna sammelt eure gemeinsamen Ausgaben an einem Ort, teilt sie nach eurer Regel und zeigt am Monatsende, wer wem wie viel schuldet.',
    note: 'Kostenlos starten. Kein Tracking. Made in Germany.',
  },
  problems: {
    heading: 'Das Problem mit gemeinsamen Ausgaben',
    intro:
      'Solange die Beträge klein sind, geht es im Bauchgefühl. Mit jedem Monat häufen sich Beträge an, die in WhatsApp, im Kopf oder in einer Excel-Datei verschwinden.',
    items: [
      {
        title: 'Wer hat was bezahlt?',
        description:
          'Einer schießt vor, der andere übernimmt das nächste Mal. Nach drei Wochen weiß keiner mehr, wer auf welchem Stand ist.',
      },
      {
        title: 'Streit über Kleinigkeiten',
        description:
          'Diskussionen entstehen nicht wegen 200 €, sondern wegen 8,49 € — weil das Gefühl der Fairness fehlt.',
      },
      {
        title: 'Belege verschwinden',
        description:
          'Kassenzettel verblassen, Online-Bestellungen liegen in fünf verschiedenen Mailboxen, Rückerstattungen werden vergessen.',
      },
      {
        title: 'Kein Monatsabschluss',
        description:
          'Ohne klaren Schnitt zieht sich der Rückstand monatelang weiter und wird zur stillen Belastung in der Beziehung.',
      },
    ],
  },
  solutions: {
    heading: 'So hilft Equna Paaren konkret',
    intro:
      'Eine globale Verteilungsregel, dann läuft das Erfassen schnell. Beide Partner sehen jederzeit den aktuellen Stand.',
    items: [
      {
        icon: 'split',
        title: 'Verteilung einmal festlegen',
        description:
          '50/50, 60/40 oder nach Einkommen — eure Regel gilt automatisch für jede gemeinsame Ausgabe.',
      },
      {
        icon: 'receipt',
        title: 'Belege direkt mitspeichern',
        description:
          'Foto vom Kassenzettel oder Screenshot der Online-Rechnung — direkt an der Ausgabe statt in der Schublade.',
      },
      {
        icon: 'calendar',
        title: 'Monatsabschluss mit einem Klick',
        description:
          'Beide bestätigen den Monat, Equna berechnet den Ausgleich. Was offen ist, ist klar — was bezahlt ist, ist abgehakt.',
      },
      {
        icon: 'home',
        title: 'Budgets für eure Bereiche',
        description:
          'Lebensmittel, Wohnen, Freizeit — legt Budgets fest und seht früh, wenn ein Bereich aus dem Ruder läuft.',
      },
      {
        icon: 'heart',
        title: 'Zwei gleichberechtigte Sichten',
        description:
          'Keine „Hauptperson" und kein „Anhang". Beide Partner haben dieselbe App, dieselben Rechte, dieselbe Sicht.',
      },
      {
        icon: 'shield',
        title: 'DSGVO-konform, kein Tracking',
        description:
          'Alle Ausgaben, Belege und Budgets liegen auf deutschen Servern. Aktuell ohne Werbung, keine Verkäufe an Dritte.',
      },
    ],
  },
  comparison: {
    heading: 'Vorher vs. mit Equna',
    columns: ['Ohne App', 'Mit Equna'],
    rows: [
      {
        topic: 'Ausgaben erfassen',
        without: 'WhatsApp-Nachrichten, Excel, Bauchgefühl',
        with: 'Eintrag in 10 Sekunden, beide sehen es sofort',
      },
      {
        topic: 'Verteilung',
        without: 'Jedes Mal neu diskutieren',
        with: 'Einmal festlegen — gilt für alles',
      },
      {
        topic: 'Belege',
        without: 'Kassenzettel verlieren sich',
        with: 'Foto direkt an der Ausgabe',
      },
      {
        topic: 'Monatsabschluss',
        without: 'Wird verschoben oder vergessen',
        with: 'Beide bestätigen, App rechnet aus',
      },
      {
        topic: 'Transparenz',
        without: 'Einer hat den Überblick, der andere nicht',
        with: 'Beide sehen denselben Stand',
      },
    ],
  },
  trust: {
    heading: 'Warum Paare Equna vertrauen',
    items: [
      {
        title: 'Made in Germany',
        description: 'Entwickelt und betrieben in Berlin. Server stehen bei IONOS in Deutschland.',
      },
      {
        title: 'DSGVO-konform',
        description: 'Eure Ausgaben, Belege und Budgets bleiben bei euch. Keine Weitergabe an Werbenetzwerke.',
      },
      {
        title: 'Kostenlos starten',
        description: 'Die Grundversion deckt alles ab, was Paare im Alltag brauchen — ohne Zeitlimit.',
      },
      {
        title: 'Kein Tracking',
        description: 'Keine Werbe-Cookies, keine versteckten Tracker. Reine App, keine Datenhandelsplattform.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Mehr zum Thema lesen',
    intro: 'Wenn du dich tiefer einlesen willst, bevor du die App lädst.',
    posts: [
      {
        title: 'Haushaltskosten fair teilen — 5 Modelle',
        description:
          '50/50, anteilig nach Einkommen, Topf-Modell und mehr: Welches Modell passt zu eurer Situation?',
        href: '/blog/haushaltskosten-fair-teilen-fuenf-modelle',
      },
      {
        title: '3-Konten-Modell und Equna — warum beides zusammengehört',
        description:
          'Das Drei-Konten-Modell regelt die Struktur, Equna regelt den Alltag. So passen beide zusammen.',
        href: '/blog/3-konten-modell-und-equna-warum-beides-zusammengehoert',
      },
      {
        title: 'Equna vs. Splitwise',
        description:
          'Wo Splitwise stark ist, wo Equna anders ansetzt — ein ehrlicher Vergleich für Paare und Familien.',
        href: '/blog/equna-vs-splitwise-fuer-paare-und-familien',
      },
    ],
  },
  faq: {
    heading: 'Häufige Fragen',
    items: [
      {
        question: 'Brauchen wir beide ein Konto?',
        answer:
          'Ja. Beide Partner laden die App, einer erstellt die Gruppe und lädt den anderen per Einladungslink ein. Danach sehen beide dieselben Ausgaben in Echtzeit.',
      },
      {
        question: 'Können wir die Verteilung später ändern?',
        answer:
          'Die globale Verteilung lässt sich jederzeit anpassen. Bei einzelnen Ausgaben kannst du außerdem von der globalen Regel abweichen, wenn z.B. ein Kauf nur einem von euch zugute kommt.',
      },
      {
        question: 'Was passiert mit dem Ausgleich am Monatsende?',
        answer:
          'Equna berechnet den Saldo. Die tatsächliche Überweisung macht ihr selbst — die App zwingt euch zu keinem Zahlungsdienstleister.',
      },
      {
        question: 'Was, wenn wir mal keinen Beleg haben?',
        answer:
          'Du kannst Ausgaben auch ohne Beleg eintragen. Für Online-Käufe reicht ein Screenshot der Bestellbestätigung oder der Bankbuchung.',
      },
      {
        question: 'Ist Equna kostenlos?',
        answer:
          'Ja. Die Grundversion enthält alles, was Paare im Alltag brauchen: Ausgaben, Verteilung, Belege (60 Tage), Monatsabschluss, drei Budget-Bereiche. Eine optionale Premium-Version mit längerer Beleg-Speicherung und Jahresbudgets ist geplant.',
      },
    ],
  },
  finalCta: {
    heading: 'Bereit, das Thema Geld geräuschlos zu machen?',
    sub: 'Equna laden, Partner einladen, Verteilung festlegen — fertig.',
  },
  uiStrings: {
    backToHome: '← Zur Startseite',
    related: 'Mehr lesen',
    legalPrivacy: 'Datenschutz',
    legalTerms: 'AGB',
    legalImprint: 'Impressum',
    legalSupport: 'Support',
    copyrightRights: 'Alle Rechte vorbehalten.',
    breadcrumbHome: 'Startseite',
    breadcrumbCurrent: 'Ausgaben teilen für Paare',
  },
}

export default function PaareSeoPage() {
  return <SeoLandingLayout content={content} />
}
